/**
 * What the budget caps card says — Story 23.4.
 *
 * `design/screens/usage-cost.html` draws one state in words: "You passed your $30
 * warning at 11:42 on Tuesday. Analysis keeps running until $50." That sentence is
 * built here from the API's own verdict (`state`), so the card never decides for
 * itself whether a cap was reached — `services/usage/caps.ts`'s `capState` is the one
 * rule, shared with the queue consumer that enforces it.
 *
 * WHAT THIS SAYS THAT THE EXPORT DOES NOT DRAW, and why:
 *
 *   · **The stop, reached.** The export's account is between its caps; an account
 *     past its stop needs a sentence, and "Analysis keeps running until $50" would be
 *     false there. It names the two ways out and nothing else.
 *   · **No time, when there is no claim.** A warning passed by spend the consumer has
 *     not yet noticed — a model check, say, or a warning set below this month's spend
 *     — has no moment to name, so the sentence leaves the moment out rather than
 *     inventing one.
 *   · **No second sentence without a stop**, because there is nothing it keeps
 *     running until.
 *
 * The time is the reader's own clock and a fixed vocabulary, not `toLocaleString`:
 * Chrome's en-GB month data already put "Sept" on this page's chart once (`10046d7`).
 */
import { formatCap } from './money'

export type CapState = 'under' | 'warned' | 'stopped'

/** `GET /v1/orgs/{oid}/usage/caps` — `packages/api/src/services/usage/caps.ts`. */
export type CapsView = {
  warnMicros: number | null
  stopMicros: number | null
  state: CapState
  warnedAt: number | null
}

const DAY_MS = 86_400_000
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/**
 * "11:42 on Tuesday" — a weekday while it is unambiguous, inside the last six days;
 * "11:42 on 3 Sep" before that, because a warning can be claimed on the 1st and read
 * on the 29th, and a switch can stay off for longer than a week.
 */
function momentOf(at: number, now: number): string {
  const date = new Date(at)
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  const day = now - at < 6 * DAY_MS ? WEEKDAYS[date.getDay()] : `${date.getDate()} ${MONTHS[date.getMonth()]}`
  return `${time} on ${day}`
}

/** "at 11:42 on Tuesday" — when a warning was passed. */
export const whenPassed = (at: number, now: number): string => `at ${momentOf(at, now)}`

/** The card's status sentence, or null when the month is under both caps. */
export function capsSentence(view: CapsView, now: number): string | null {
  if (view.state === 'stopped' && view.stopMicros !== null) {
    return `You reached your ${formatCap(view.stopMicros)} stop, so analysis is paused until your plan renews or you raise it.`
  }
  if (view.state === 'warned' && view.warnMicros !== null) {
    const when = view.warnedAt === null ? '' : ` ${whenPassed(view.warnedAt, now)}`
    const running = view.stopMicros === null ? '' : ` Analysis keeps running until ${formatCap(view.stopMicros)}.`
    return `You passed your ${formatCap(view.warnMicros)} warning${when}.${running}`
  }
  return null
}

/** The zero state's "Your caps are already set" — said only when both are. */
export const bothCapsSet = (view: CapsView | null): boolean =>
  view !== null && view.warnMicros !== null && view.stopMicros !== null

/* ── the kill switch — Story 23.5 ───────────────────────────────────────── */

/** `GET·PUT /v1/orgs/{oid}/usage/switch` — `packages/api/src/services/usage/caps.ts`. */
export type SwitchView = { off: boolean; since: number | null }

/**
 * The stop control's status line while AI spend is off, or null while it is on.
 *
 * The export draws only the control at rest, so this is the one sentence the
 * switched-off card needs: that it is off, and since when. What that means for leads
 * is already the card's own drawn sub-line, which stays.
 */
export function switchSentence(view: SwitchView, now: number): string | null {
  if (!view.off) return null
  return view.since === null ? 'AI spend is switched off.' : `AI spend has been off since ${momentOf(view.since, now)}.`
}
